import React from 'react';

import Data from 'data';

import urlBackground from 'assets/images/skills-hd.png';
import urlBackgroundSingle from 'assets/images/hd-skills-embed.png';
import urlArrows from 'assets/images/arrows.png';
import { gridPos } from './utils';

const loadImage = url => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = e => reject(e);
  img.src = url;
});

const arrowRc = {
  skillBox: [0, 0, 51, 51],
  levelBox: [52, 0, 24, 24],
  arrowS: [152, 0, 23, 154],
  arrowS2: [52, 25, 23, 18],
  arrowE: [0, 52, 150, 23],
  arrowW: [0, 76, 150, 23],
  arrowSW: [0, 100, 64, 70],
  arrowSE: [65, 100, 64, 70],
};

const tabParams = [
  {
    offsetY: 36,
    levelX: 42,
    levelY: 43,
    levelTrim: [1, 2, 1, 1],
    arrowS2H: 18,
    arrowSdH: 51,
    arrowSWx: 45,
    arrowSWy: 3,
    arrowSEx: 4,
    arrowSEy: 3,
    arrowDiagW1: 45,
    arrowDiagW2: 45,
    arrowDiagDy: 0,
    arrowH1: 51,
    arrowH2: 53,
    arrowH1y: 12,
    arrowH2y: 12,
  },
  {
    offsetY: 0,
    levelX: 51,
    levelY: 27,
    levelTrim: [0, 0, 0, 0],
    arrowS2H: 17,
    arrowSdH: 53,
    arrowSWx: 55,
    arrowSWy: -4,
    arrowSEx: -4,
    arrowSEy: -4,
    arrowDiagW1: 45,
    arrowDiagW2: 64,
    arrowDiagDy: 10,
    arrowH1: 51,
    arrowH2: 75,
    arrowH1y: 8,
    arrowH2y: 12,
  },
];

export default function SkillsBackground({ charClass, page }) {
  const canvasRef = React.useRef();
  const loadBg = React.useRef();
  if (!loadBg.current) loadBg.current = loadImage(page ? urlBackgroundSingle : urlBackground);
  const loadArrows = React.useRef();
  if (!loadArrows.current) loadArrows.current = loadImage(urlArrows);
  React.useEffect(() => {
    Promise.all([loadBg.current, loadArrows.current]).then(([bg, arrows]) => {
      if (!canvasRef.current) return;
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      const params = tabParams[page ? 1 : 0];

      const skills = Object.values(Data.skills).filter(skill => skill.charclass === charClass)
        .map(skill => ({skill, desc: Data.skillDesc[skill.skilldesc]})).filter(item => item.desc);

      const arrowList = [];
      for (const { skill, desc } of skills) {
        if (page && desc.skillpage !== page) continue;

        const pos = gridPos(page ? -1 : desc.skillpage, desc.skillrow, desc.skillcolumn, true);
        pos.top += params.offsetY;

        for (const reqId of [skill.reqskill1, skill.reqskill2, skill.reqskill3]) {
          const req = Data.skillDesc[Data.skills[reqId]?.skilldesc];
          if (!req) continue;
          const rpos = gridPos(page ? -1 : req.skillpage, req.skillrow, req.skillcolumn, true);
          rpos.top += params.offsetY;
          if (req.skillrow === desc.skillrow) {
            const diff = desc.skillcolumn - req.skillcolumn;
            const extraPx = Math.abs(diff) <= 1 ? params.arrowH1 : params.arrowH2;
            const extraPy = Math.abs(diff) <= 1 ? params.arrowH1y : params.arrowH2y;
            if (diff < 0) {
              const [x, y, , h] = arrowRc.arrowW;
              const drawW = rpos.left - pos.left - extraPx;
              arrowList.push([0, x, y, drawW, h, rpos.left - 1 - drawW, pos.top + extraPy, drawW, h]);
            } else {
              const [, y, w, h] = arrowRc.arrowE;
              const drawW = pos.left - rpos.left - extraPx;
              arrowList.push([0, w - drawW, y, drawW, h, rpos.left + 50, pos.top + extraPy, drawW, h]);
            }
          } else if (req.skillcolumn === desc.skillcolumn) {
            if (desc.skillrow - req.skillrow <= 1) {
              const [x, y, w, h] = arrowRc.arrowS2;
              arrowList.push([0, x, y + h - params.arrowS2H, w, params.arrowS2H, rpos.left + 13, rpos.top + 49, w, params.arrowS2H]);
            } else {
              const [x, , w, h] = arrowRc.arrowS;
              const drawH = pos.top - rpos.top - params.arrowSdH;
              arrowList.push([0, x, h - drawH, w, drawH, rpos.left + 13, rpos.top + 49, w, drawH]);
            }
          } else {
            const drawW = (desc.skillrow - req.skillrow <= 1 ? params.arrowDiagW1 : params.arrowDiagW2);
            const hasAbove = skills.some(sk => sk.desc.skillpage === desc.skillpage && sk.desc.skillrow === desc.skillrow - 1 && sk.desc.skillcolumn === desc.skillcolumn);
            const dY = hasAbove ? params.arrowDiagDy : 0;
            if (desc.skillcolumn < req.skillcolumn) {
              const [x, y, , h] = arrowRc.arrowSW;
              arrowList.push([1, x, y, drawW, h, pos.left + params.arrowSWx, pos.top + params.arrowSWy - h + dY, drawW, h]);
            } else {
              const [x, y, w, h] = arrowRc.arrowSE;
              arrowList.push([1, x + w - drawW, y, drawW, h, pos.left + params.arrowSEx - drawW, pos.top + params.arrowSEy - h + dY, drawW, h]);
            }
          }
        }

        const levelBox = [...arrowRc.levelBox];
        levelBox[0] += params.levelTrim[0];
        levelBox[1] += params.levelTrim[1];
        levelBox[2] -= params.levelTrim[0] + params.levelTrim[2];
        levelBox[3] -= params.levelTrim[1] + params.levelTrim[3];
        arrowList.push([2, ...levelBox, pos.left + params.levelX, pos.top + params.levelY, ...levelBox.slice(2)]);
        arrowList.push([3, ...arrowRc.skillBox, pos.left - 1, pos.top - 2, ...arrowRc.skillBox.slice(2)]);
      }

      ctx.globalCompositeOperation = 'source-over';
      arrowList.sort((a, b) => a[0] - b[0]);
      for (const params of arrowList) {
        ctx.drawImage(arrows, ...params.slice(1));
      }

      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(bg, 0, 0);
    });
  }, [charClass, page]);

  return <canvas width={page ? 341 : 696} height={page ? 478 : 468} className="d2p-skills-background-hd" ref={canvasRef}/>;
}
